import { FC, useCallback } from 'react';
import styled from 'styled-components';

import { SelectAssetCategory } from '@/entities/assets/components/SelectAssetCategory/index';
import { SelectCountryV2 } from '@/shared/components/forms/fields/SelectCountryV2';
import { AssetCategory } from '@/shared/lib/sj-orm';
import { Icon } from '@/shared/uikit/Icon';
import { Input } from '@/shared/uikitV2/Input';
import { Select } from '@/shared/uikitV2/Select';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { FilterOptions, FilterValue } from '../../types';

type AssetFilterFieldsProps = {
  hideSearch?: boolean;
  filterOptions: FilterOptions;

  search: string;
  valueFrom: number | null;
  valueTo: number | null;
  category: AssetCategory[] | null;
  country: string[] | null;
  currency: string[] | null;
  setSearch: (value: string) => void;
  setCategory: (category: FilterValue['category']) => void;
  setCountry: (country: FilterValue['country']) => void;
  setCurrency: (currency: FilterValue['currency']) => void;
  setValueFrom: (value: FilterValue['valueFrom']) => void;
  setValueTo: (value: FilterValue['valueTo']) => void;
  resetOneOf: (key: keyof FilterValue) => void;
};

export const AssetFilterFields: FC<AssetFilterFieldsProps> = ({
  filterOptions,
  hideSearch,
  search,
  valueFrom,
  valueTo,
  category,
  country,
  currency,
  setSearch,
  setCategory,
  setCountry,
  setCurrency,
  setValueFrom,
  setValueTo,
  resetOneOf,
}) => {
  const { t } = useTranslation('pages/pageAssets', { keyPrefix: 'filter' });

  const normalizeOptions = useCallback(
    (items: string[]) =>
      items.map((item) => ({
        value: item,
        content: { label: item },
      })),
    [],
  );

  const getInputReset = useCallback(
    (value: string | number | null, field: 'search' | 'valueFrom' | 'valueTo') =>
      value ? (
        <Icon size="s20" style={{ cursor: 'pointer' }} name="x" onClick={() => resetOneOf(field)} />
      ) : undefined,
    [resetOneOf],
  );

  return (
    <>
      {!hideSearch && (
        <Input
          label={t('searchPlaceholder')}
          value={search}
          onChange={(ev) => setSearch(ev.target.value)}
          iconBefore={<Icon size="s20" name="magnifying-glass" />}
          iconAfter={getInputReset(search, 'search')}
        />
      )}
      <SelectAssetCategory
        label={t('categoriesPlaceholder')}
        value={category}
        options={normalizeOptions(filterOptions.categories)}
        multiple
        onChange={(c) => setCategory(c as AssetCategory[])}
      />
      <SelectCountryV2
        multiple
        name="country"
        label={t('countriesPlaceholder')}
        value={country}
        options={normalizeOptions(filterOptions.countries)}
        onChange={(value) => setCountry(value as string[])}
      />
      <Select
        label={t('currenciesPlaceholder')}
        value={currency}
        options={normalizeOptions(filterOptions.currencies)}
        multiple
        onChange={(value) => setCurrency(value as string[])}
      />
      <StyledWrapper>
        <Input
          type="number"
          label={t('valueFromPlaceholder')}
          value={valueFrom || ''}
          onChange={(ev) => setValueFrom(Number(ev.target.value) ?? '')}
          iconAfter={getInputReset(valueFrom, 'valueFrom')}
        />
      </StyledWrapper>
      <Input
        type="number"
        label={t('valueToPlaceholder')}
        value={valueTo || ''}
        onChange={(ev) => setValueTo(Number(ev.target.value) ?? '')}
        iconAfter={getInputReset(valueTo, 'valueTo')}
      />
    </>
  );
};

const StyledWrapper = styled.div`
  position: relative;

  &::after {
    content: '--';
    position: absolute;
    right: -12px;
    top: 17px;
  }
`;
